import React, { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from 'static';

import {
  FileKind,
  StyledAttachmentCard,
  StyledAttachmentInfo,
  StyledAttachmentMeta,
  StyledAttachmentName,
  StyledAttachmentRemove,
  StyledAttachments,
  StyledAttachmentsGrid,
  StyledAttachmentsHeader,
  StyledAttachmentsTitle,
  StyledAttachmentThumb,
  StyledError,
  StyledFileBadge,
  StyledFileIcon,
  StyledUsage,
  StyledUsageBar,
} from './styled';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic'];

const KIND_BY_EXTENSION: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'doc',
  docx: 'doc',
  txt: 'doc',
  rtf: 'doc',
  odt: 'doc',
  pages: 'doc',
  xls: 'sheet',
  xlsx: 'sheet',
  csv: 'sheet',
  ods: 'sheet',
  numbers: 'sheet',
  ppt: 'slides',
  pptx: 'slides',
  key: 'slides',
  odp: 'slides',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
};

const getExtension = (name: string) => {
  const parts = (name || '').split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const getKind = (extension: string): FileKind => {
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  return KIND_BY_EXTENSION[extension] || 'other';
};

export const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface AttachmentsProps {
  files: File[];
  maxBytes: number;
  error?: string;
  onRemove: (file: File) => void;
}

const Attachments: FC<AttachmentsProps> = ({
  files,
  maxBytes,
  error,
  onRemove,
}) => {
  const { t } = useTranslation();

  const previews = useMemo(
    () =>
      files.map((file) =>
        getKind(getExtension(file.name)) === 'image' && file instanceof Blob
          ? URL.createObjectURL(file)
          : null,
      ),
    [files],
  );

  useEffect(
    () => () => previews.forEach((url) => url && URL.revokeObjectURL(url)),
    [previews],
  );

  if (!files.length && !error) return null;

  const totalBytes = files.reduce((sum, file) => sum + (file?.size ?? 0), 0);
  const usage = Math.min(totalBytes / maxBytes, 1);

  return (
    <StyledAttachments>
      {files.length > 0 && (
        <StyledAttachmentsHeader>
          <StyledAttachmentsTitle>
            {t(
              files.length === 1
                ? 'email.attachmentsCount_one'
                : 'email.attachmentsCount_other',
              { count: files.length },
            )}
          </StyledAttachmentsTitle>
          <StyledUsage overLimit={totalBytes > maxBytes}>
            {t('email.attachmentsUsage', {
              used: formatBytes(totalBytes),
              max: formatBytes(maxBytes),
            })}
            <StyledUsageBar
              progress={usage}
              overLimit={totalBytes > maxBytes}
              aria-hidden="true"
            />
          </StyledUsage>
        </StyledAttachmentsHeader>
      )}
      <StyledAttachmentsGrid>
        {files.map((file, index) => {
          const extension = getExtension(file.name);
          const kind = getKind(extension);
          return (
            <StyledAttachmentCard
              key={`${file.name}-${index}`}
              title={file.name}
            >
              {previews[index] ? (
                <StyledAttachmentThumb src={previews[index]} alt="" />
              ) : (
                <StyledFileIcon>
                  <StyledFileBadge kind={kind}>
                    {(extension || 'file').slice(0, 4).toUpperCase()}
                  </StyledFileBadge>
                </StyledFileIcon>
              )}
              <StyledAttachmentInfo>
                <StyledAttachmentName>{file.name}</StyledAttachmentName>
                <StyledAttachmentMeta>
                  {formatBytes(file.size)}
                </StyledAttachmentMeta>
              </StyledAttachmentInfo>
              <StyledAttachmentRemove
                type="button"
                aria-label={t('email.removeAttachment', { name: file.name })}
                onClick={() => onRemove(file)}
              >
                <CloseIcon />
              </StyledAttachmentRemove>
            </StyledAttachmentCard>
          );
        })}
      </StyledAttachmentsGrid>
      {error && <StyledError>{error}</StyledError>}
    </StyledAttachments>
  );
};

export default Attachments;
